'use client';

import React, { useState } from 'react';

import { ClickableDiv } from '@wwe-portal/ui/components/button';
import { IcoMoonIcon } from '@wwe-portal/ui/components/icons';
import WarningIcon from '@wwe-portal/ui/components/icons/claim-all-icon';
import { Skeleton } from '@wwe-portal/ui/components/skeleton';
import { type OfferCardProps } from '@wwe-portal/ui/features/offer';
import { OfferQuantity } from '@wwe-portal/ui/features/offer/component/offer-quantity';
import type { SubscriptionTier } from '@wwe-portal/entities/subscription.type';
import { useTimeLeft } from '@wwe-portal/ui/lib/hooks';

import BuyButton from './buy-button';
import { ExclusiveOfferBanner } from './exclusive-offer-banner';
import type { MoreDescriptionProps } from './more-description';
import MoreDescription from './more-description';
import { TierBadge } from './tier-badge';

export interface ExclusiveItemProps {
  id: string;
  tier: SubscriptionTier | undefined;
  offerData: OfferCardProps;
  endDate?: Date;
  moreInfoData: MoreDescriptionProps;
  isBlocked: boolean;
}

interface ExclusiveItemMobileProps {
  exclusiveItem: ExclusiveItemProps;
  onClick: (offer: ExclusiveItemProps) => void;
  onShowOfferCard: (offer: ExclusiveItemProps) => void;
  isLoading?: boolean;
}

interface ExclusiveItemDesktopProps {
  exclusiveItem: ExclusiveItemProps;
  onClick: (offer: ExclusiveItemProps, isMoreDescription?: boolean) => void;
  isLoading?: boolean;
}

export function ExclusiveItemMobileSkeleton() {
  return (
    <div className="font-poppins bg-bgColor2 flex h-full w-full flex-col items-center justify-between rounded-[10px] px-[10px] pb-[10px] pt-[15px]">
      <div className="flex w-full grow items-start justify-between gap-3 pb-5">
        <div className="h-12 w-[80px]">
          <Skeleton className="bg-back200 aspect-single-1 font-poppins relative rounded md:aspect-auto md:min-h-40" />
        </div>
        <div className="grow">
          <div className="font-poppins text-text300 max-w-[165px] break-words text-xs font-medium leading-4">
            <Skeleton className="bg-back200 h-5 w-[100px]" />
          </div>
          <div className="flex items-center justify-start pt-1">
            <Skeleton className="bg-back200 mr-2 h-4 w-8" />
            <Skeleton className="bg-back200 h-4 w-10" />
          </div>
        </div>
        <Skeleton className="bg-back200 h-4 w-12" />
      </div>
      <Skeleton className="bg-silvertier h-10 w-full" />
    </div>
  );
}

export function ExclusiveItemMobile({
  exclusiveItem,
  onClick,
  onShowOfferCard,
  isLoading = false,
}: Readonly<ExclusiveItemMobileProps>) {
  const { offerData, tier } = exclusiveItem;
  return (
    <div className="font-poppins bg-back300 flex h-full w-full flex-col items-center justify-between rounded-[6px] px-[10px] pb-[10px] pt-[15px]">
      <ClickableDiv
        className="flex w-full grow items-start justify-between gap-3 pb-4"
        onClick={() => onShowOfferCard(exclusiveItem)}
      >
        <div className="h-12 w-[80px]">
          <ExclusiveOfferBanner {...offerData} isMobile />
        </div>
        <div className="grow p-0">
          <div className="text-text300 font-poppins max-w-[165px] break-words text-xs font-medium leading-4">
            {offerData.title}
          </div>
          <div className="flex items-center justify-start gap-x-1 pt-1">
            <div className="font-poppins text-goldtier flex items-center justify-center gap-x-1 text-xs font-semibold leading-[18px]">
              {offerData.price}
            </div>
            {offerData.quantity ? (
              <div className="font-poppins text-inlineTextColor12 text-2xs font-normal leading-[15px]">
                (<OfferQuantity quantity={offerData.quantity} />)
              </div>
            ) : null}
          </div>
        </div>
        <TierBadge tier={tier} />
      </ClickableDiv>
      <BuyButton
        exclusiveItem={exclusiveItem}
        isBlocked={exclusiveItem.isBlocked}
        isLoading={isLoading}
        isMobile
        offerData={offerData}
        onClick={onClick}
      />
    </div>
  );
}

export function ExclusiveItemDesktopSkeleton() {
  return (
    <div className="wwe bg-bgColor2 font-poppins relative h-[324px] w-full rounded-[10px]">
      <div className="flex h-full flex-col justify-between">
        <div className="w-full">
          <Skeleton className="aspect-single-1 font-poppins bg-back200 relative md:aspect-auto md:min-h-40" />
          <div className="flex items-start justify-between p-3 pt-5">
            <div className="text-text300 w-full max-w-[170px] break-words text-xs font-medium leading-5">
              <Skeleton className="text-text300 bg-back200 h-4 break-words text-xs font-medium leading-5" />
            </div>

            <div className="flex h-[18px] w-[47px] items-center justify-center rounded-[4px] text-[8px] font-normal">
              <Skeleton className="bg-back200 h-4 w-full" />
            </div>
          </div>
        </div>
        <Skeleton className="bg-back200 m-3 h-4 w-20" />
        <div className="grid grid-flow-col gap-3 p-3">
          <Skeleton className="3xl:min-w-[100px] bg-goldtier font-poppins h-8 rounded-md text-xs font-semibold leading-[18px] 2xl:min-w-28" />
          <Skeleton className="3xl:min-w-[46px] bg-back200 flex h-8 w-full items-center justify-center rounded text-xs font-normal leading-[18px] 2xl:min-w-14" />
          <Skeleton className="3xl:min-w-8 bg-back200 flex h-8 items-center justify-center text-xs font-normal leading-[18px] 2xl:min-w-12" />
        </div>
      </div>
    </div>
  );
}

export function ExclusiveItemDesktop({
  exclusiveItem,
  onClick,
  isLoading = false,
}: Readonly<ExclusiveItemDesktopProps>) {
  const { offerData, tier, moreInfoData } = exclusiveItem;
  const [isMoreDescription, setIsMoreDescription] = useState(false);
  const isDark = document.body.getAttribute('data-theme') === 'dark';
  const { getTimeText } = useTimeLeft(exclusiveItem.endDate);

  return (
    <div className="font-poppins bg-back300 md:bg-bgColor2 relative h-[324px] w-full rounded-[10px]">
      {isMoreDescription && !isDark ? null : (
        <div className="absolute inset-0 flex h-full flex-col justify-between rounded-[10px]">
          <div className="www-w-full">
            <ExclusiveOfferBanner {...offerData} />
            <div className="flex items-start justify-between p-3 pt-5">
              <div className="text-text300 max-w-[170px] break-words text-xs font-medium leading-5">
                {offerData.title}
              </div>
              <TierBadge
                className="flex items-center justify-center rounded-[4px] border-[1px] border-solid text-[9px] font-normal"
                tier={tier}
              />
            </div>
          </div>
          {Boolean(getTimeText()) && (
            <div className="flex w-[200px] items-center justify-start pl-3">
              <IcoMoonIcon className="text-secondary size-3" iconName="timer" />
              <div className="text-secondary font-poppins pl-1 text-xs font-medium leading-5">
                {getTimeText()}
              </div>
            </div>
          )}
          <div className="3xl:gap-[5px] flex justify-between gap-[10px] px-[15px] py-[14px]">
            <BuyButton
              exclusiveItem={exclusiveItem}
              isBlocked={exclusiveItem.isBlocked}
              isLoading={isLoading}
              isMobile={false}
              offerData={offerData}
              onClick={onClick}
            />
            {offerData.quantity ? (
              <div className="text-text600 border-text600 flex h-8 grow items-center justify-center gap-[6px] rounded border-[1px] border-solid p-[6px] text-xs font-normal leading-[18px]">
                <OfferQuantity quantity={offerData.quantity} />
              </div>
            ) : null}
            <ClickableDiv
              className="text-text600 border-text600 flex h-8 grow items-center justify-center rounded border-[1px] border-solid p-[6px] text-xs font-normal leading-[18px]"
              onClick={() => setIsMoreDescription(!isMoreDescription)}
            >
              <WarningIcon className="fill-text600 text-back300 size-[14px]" />
            </ClickableDiv>
          </div>
        </div>
      )}
      {Boolean(exclusiveItem.offerData) && isMoreDescription ? (
        <MoreDescription
          {...moreInfoData}
          actionBtn={
            <BuyButton
              exclusiveItem={exclusiveItem}
              isBlocked={exclusiveItem.isBlocked}
              isLoading={isLoading}
              isMobile={false}
              offerData={offerData}
              onClick={() => onClick(exclusiveItem, true)}
            />
          }
          isDark={isDark}
          onClose={() => setIsMoreDescription(!isMoreDescription)}
        />
      ) : null}
    </div>
  );
}
